/**
 * 「系统参数运维」页的三张运维触发卡。
 *
 * 与配置区的区别不只是排版：这三个按钮点下去会**真的下发任务**（创建 Job、
 * 批量改状态、下发建图），所以视觉上统一用 amber 色条（``variant="ops"``）、
 * 每个都套 Popconfirm 二次确认，与"改个配置值"明确区分开。
 *
 * 三卡都把最近一次返回结果留在卡内（jobId / 扫描统计 / 后端返回串），
 * 因为 message 会自己消失，而运维同学经常要回头抄 jobId 去查。
 */
import { useEffect, useRef, useState } from 'react';
import { App, Button, Popconfirm } from 'antd';
import { DeleteOutlined, ReloadOutlined, SearchOutlined, ThunderboltOutlined } from '@ant-design/icons';

import {
  BulkRefreshRunningJobsVo, DailyJobTriggerVo, PartitionCleanupJobStatus,
  getCleanupExpiredPartitionsStatus, triggerBulkRefreshRunningJobs, triggerCleanupExpiredPartitions,
  triggerDailyJob, triggerMapGenerateRecent,
} from '@apis/simulation';

import { Panel, Stat } from './shared';

/** 运维操作 · 手动触发 daily 仿真（等价每天 01:00 cron）。 */
export function DailyJobTriggerCard({ index }: { index: number }) {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState<DailyJobTriggerVo | null>(null);

  const handleTrigger = async () => {
    setLoading(true);
    try {
      const result = await triggerDailyJob();
      setLastResult(result);
      message.success(`已下发 daily 仿真，jobId=${result.jobId ?? '-'}`);
    } catch (err) {
      message.error('触发 daily 仿真失败，详见浏览器控制台');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Panel
      variant="ops"
      kicker="运维 · DAILY 仿真"
      title="手动触发 daily 仿真"
      index={index}
      info={(
        <span>
          等价于每天 <code>01:00</code> 自动 cron 的一次性下发，使用默认参数创建 Job 并
          发送到调度平台。适用于 cron 异常补跑、回归验证、临时联调；与定时任务互不影响
          （后端 Redis 互斥锁兜底，不会重复创建）。
        </span>
      )}
    >
      <div className="sc-ops-desc">
        以默认参数创建 Job 并下发调度平台；用于 cron 补跑 / 回归 / 联调，Redis 锁防重复。
      </div>
      <Popconfirm
        title="确认立即触发 daily 仿真？"
        description="将以默认参数创建一个新的 Job 并下发到调度平台。"
        okText="确认触发"
        cancelText="取消"
        onConfirm={handleTrigger}
      >
        {/* 尺寸与字重见 index.less 的 .sc-ops-fire：这颗按钮点下去会真的创建 Job，
            不能让它在视觉上轻于配置卡里那颗小号描边的「重置」。 */}
        <Button
          type="primary"
          className="sc-ops-fire"
          icon={<ThunderboltOutlined />}
          loading={loading}
        >
          立即触发
        </Button>
      </Popconfirm>
      {lastResult && (
        <div className="sc-result">
          <Stat label="jobId" value={lastResult.jobId ?? '-'} />
          <Stat label="dispatchJobId" value={lastResult.dispatchJobId ?? '-'} />
        </div>
      )}
    </Panel>
  );
}

/** 运维操作 · 批量刷新未完成 Job 状态（Kafka 漏单 / 调度延迟兜底）。 */
export function BulkRefreshRunningJobsCard({ index }: { index: number }) {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState<BulkRefreshRunningJobsVo | null>(null);

  const handleTrigger = async () => {
    setLoading(true);
    try {
      const result = await triggerBulkRefreshRunningJobs();
      setLastResult(result);
      if (result.lock_busy) {
        message.warning('已有运维同学在批量刷新中，请稍后再试');
      } else if (result.scanned === 0) {
        message.success('当前没有未完成的 Job，无需刷新');
      } else {
        const tail = result.has_more
          ? `（剩余 ${result.total_running_before - result.scanned} 个未处理，请再次触发分批刷新）`
          : '';
        message.success(
          `已扫描 ${result.scanned} 个 Job：刷新 ${result.refreshed} / 跳过 ${result.skipped} / 失败 ${result.failed}${tail}`,
        );
      }
    } catch (err) {
      message.error('批量刷新失败，详见浏览器控制台');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // 锁被占用那轮什么都没做，统计全是 0，展示出来只会误导
  const showResult = lastResult && !lastResult.lock_busy;

  return (
    <Panel
      variant="ops"
      kicker="运维 · 状态对齐"
      title="批量刷新未完成 Job 状态"
      index={index}
      info={(
        <span>
          扫描所有 <code>pending / queued / running</code> 状态的 Job，
          拉取调度平台最新结果同步本地状态。适用于：Kafka 漏单兜底、批量评测后强制对齐汇总。
          后端有全局 Redis 锁，多人/多 Pod 并发触发也只会执行一轮；单次最多 200 个，
          超过会返回剩余数量提示分批触发。
        </span>
      )}
    >
      <div className="sc-ops-desc">
        拉取调度平台最新结果同步 <code>pending/queued/running</code> 的 Job；单次最多 200 个。
      </div>
      <Popconfirm
        title="确认立即批量刷新？"
        description="将拉取调度平台最新结果同步所有未完成 Job 的本地状态。"
        okText="确认刷新"
        cancelText="取消"
        onConfirm={handleTrigger}
      >
        <Button
          type="primary"
          className="sc-ops-fire"
          icon={<ReloadOutlined />}
          loading={loading}
        >
          立即批量刷新
        </Button>
      </Popconfirm>
      {showResult && lastResult && (
        <div className="sc-result">
          <Stat label="扫描 / 总未完成" value={`${lastResult.scanned} / ${lastResult.total_running_before}`} />
          <Stat label="刷新成功" value={lastResult.refreshed} tone="ok" />
          <Stat label="跳过" value={lastResult.skipped} />
          <Stat label="失败" value={lastResult.failed} tone={lastResult.failed > 0 ? 'bad' : undefined} />
          {lastResult.has_more && (
            <Stat label="剩余未处理" value={lastResult.total_running_before - lastResult.scanned} tone="warn" />
          )}
          {lastResult.failed_job_ids.length > 0 && (
            <Stat label="失败 jobId" value={lastResult.failed_job_ids.join(', ')} tone="bad" />
          )}
        </div>
      )}
    </Panel>
  );
}

/** 运维操作 · 最近 6 小时地图生成。 */
export function MapGenerateRecentCard({ index }: { index: number }) {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState<string | null>(null);

  const handleTrigger = async () => {
    setLoading(true);
    try {
      const result = await triggerMapGenerateRecent();
      setLastResult(result);
      if (result.includes('already running')) {
        message.warning('已有地图生成任务在运行中，请稍后再试');
      } else {
        message.success('已下发最近 6 小时地图生成任务');
      }
    } catch (err) {
      message.error('触发地图生成失败，详见浏览器控制台');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Panel
      variant="ops"
      kicker="运维 · 地图生成"
      title="触发最近地图生成"
      index={index}
      info={(
        <span>
          固定 <code>months=6</code> / <code>storage=bos</code> / 最近 <code>6 小时</code>{' '}
          时间窗，查询地图并下发 <code>generate-map</code> 任务。适用于地图数据补跑、
          临时手动补建图；后端 Redis 锁兜底，已有任务在跑时不会重复下发。
        </span>
      )}
    >
      <div className="sc-ops-desc">
        固定 <code>months=6 / storage=bos /</code> 最近 6 小时窗口下发 <code>generate-map</code>。
      </div>
      <Popconfirm
        title="确认触发最近 6 小时地图生成？"
        description="将按最近 6 小时时间窗查询并下发 generate-map 任务。"
        okText="确认触发"
        cancelText="取消"
        onConfirm={handleTrigger}
      >
        <Button
          type="primary"
          className="sc-ops-fire"
          icon={<ThunderboltOutlined />}
          loading={loading}
        >
          触发地图生成
        </Button>
      </Popconfirm>
      {lastResult && (
        <div className="sc-result">
          <Stat label="最近返回" value={<code style={{ wordBreak: 'break-all' }}>{lastResult}</code>} />
        </div>
      )}
    </Panel>
  );
}

/**
 * 运维操作 · 清理过期分区数据 + 产物 BOS（冷热分离 v2 二期）。
 *
 * 两颗按钮明确区隔风险：
 * - 「预览（dry-run）」蓝色描边，只统计命中的过期分区、不删任何东西，首上线务必先点这颗；
 * - 「真删」红色，DETACH→BOS GC→DROP，二次确认文案写明不可逆 + 只删产物不碰场景/录制。
 *
 * 触发后进入 5s 轮询状态接口，把进度/结果留在卡内（后端 Redis 全局互斥 + 持久进度，
 * 刷新页面也能续看；运行中锁 2h 过期兜底）。
 */
export function PartitionCleanupCard({ index }: { index: number }) {
  const { message } = App.useApp();
  const [triggering, setTriggering] = useState(false);
  const [status, setStatus] = useState<PartitionCleanupJobStatus | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const poll = async () => {
    try {
      const st = await getCleanupExpiredPartitionsStatus();
      setStatus(st);
      if (st.status !== 'running') {
        stopPolling();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const startPolling = () => {
    stopPolling();
    void poll();
    timerRef.current = setInterval(poll, 5000);
  };

  // 进页面先拉一次：可能有别人/上次触发的任务仍在跑或刚跑完，续看进度
  useEffect(() => {
    void (async () => {
      const st = await getCleanupExpiredPartitionsStatus().catch(() => null);
      if (st) {
        setStatus(st);
        if (st.status === 'running') startPolling();
      }
    })();
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTrigger = async (dryRun: boolean) => {
    setTriggering(true);
    try {
      const res = await triggerCleanupExpiredPartitions(dryRun, 3);
      if (res.status && res.status !== 'idle' && !res.sessionId) {
        message.warning('已有分区清理任务在运行中，请稍后查看进度');
      } else {
        message.success(dryRun ? '已提交预览（dry-run），仅统计不删除' : '已提交清理任务，正在后台执行');
      }
      startPolling();
    } catch (err) {
      message.error('触发分区清理失败，详见浏览器控制台');
      console.error(err);
    } finally {
      setTriggering(false);
    }
  };

  const running = status?.status === 'running';
  const p = status?.progress;

  return (
    <Panel
      variant="ops"
      kicker="运维 · 分区 GC"
      title="清理过期分区数据 + 产物"
      index={index}
      info={(
        <span>
          清理超过保留期（默认 <code>3 个月</code>）的 4 张分区表数据，并按
          <code>evaluation_result.address</code> 删除对应 <b>产物</b> BOS 对象。机制：
          DETACH 过期子分区为 <code>_cold_*</code> 冷备表（持久 GC 队列、崩溃可续跑）→
          仅评测结果按前缀删产物 → 删净后 DROP。🚨 只删产物，
          <b>绝不碰场景 / 录制数据</b>（它们在不同的桶）。DDL 需表 owner 权限，
          不足会在下方状态里上报「权限不足」。
        </span>
      )}
    >
      <div className="sc-ops-desc">
        先「预览」看命中多少分区/行，确认无误再「真删」。真删不可逆，只删评测产物、不碰场景/录制。
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Popconfirm
          title="预览待清理的过期分区？"
          description="只统计命中的过期分区与估算行数，不做任何删除。"
          okText="开始预览"
          cancelText="取消"
          onConfirm={() => handleTrigger(true)}
        >
          <Button icon={<SearchOutlined />} loading={triggering} disabled={running}>
            预览（dry-run）
          </Button>
        </Popconfirm>
        <Popconfirm
          title="确认真删过期分区数据 + 产物？"
          description="将 DETACH→删产物 BOS→DROP，操作不可逆。只删评测产物，不碰场景/录制数据。建议先跑一次预览。"
          okText="确认真删"
          okButtonProps={{ danger: true }}
          cancelText="取消"
          onConfirm={() => handleTrigger(false)}
        >
          <Button danger type="primary" icon={<DeleteOutlined />} loading={triggering} disabled={running}>
            真删
          </Button>
        </Popconfirm>
      </div>
      {status && status.status !== 'idle' && (
        <div className="sc-result">
          <Stat
            label="状态"
            value={running ? '运行中…' : status.status}
            tone={status.status === 'failed' ? 'bad' : status.status === 'completed' ? 'ok' : 'warn'}
          />
          <Stat label="模式" value={status.dryRun ? '预览(dry-run)' : '真删'} />
          {p && (
            <>
              <Stat label="日期组 已处理/命中" value={`${p.partitionsProcessed} / ${p.partitionsTotal}`} />
              {!status.dryRun && (
                <>
                  <Stat label="已 GC 评测行" value={p.rowsGced} />
                  <Stat label="删产物前缀 / 对象" value={`${p.bosPrefixesDeleted} / ${p.bosObjectsDeleted}`} />
                  <Stat label="DROP 分区表" value={p.partitionsDropped} tone="ok" />
                  {p.skipped > 0 && <Stat label="跳过(语义不明未删)" value={p.skipped} tone="warn" />}
                </>
              )}
            </>
          )}
          {status.message && <Stat label="错误" value={<code style={{ wordBreak: 'break-all' }}>{status.message}</code>} tone="bad" />}
        </div>
      )}
    </Panel>
  );
}
